import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import Grid from '@mui/material/Grid';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useTranslation } from 'react-i18next';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';

const Wrapper = styled.div`
  position: relative;
  background: ${PALETTE.white};
`;

type StyledCollapseProps = {
  $expanded: boolean;
};
const StyledCollapse = styled(Collapse)<StyledCollapseProps>`
  background: ${PALETTE.white};
  border: 1px solid #d6e0ea;
  ${(props) =>
    props.$expanded &&
    css`
      .MuiCollapse-wrapperInner {
        padding-bottom: 50px;
      }
    `}
`;

const ContainerGrid = styled(Grid)`
  padding: 10px 20px;
`;

const ConditionItem = styled.div`
  width: 100%;
  padding: 10px 10px 0 10px;
  background: ${PALETTE.white};
`;

const TitleGrid = styled(Grid)`
  max-width: 120px;
  border-right: 1px solid #cdd8d9;
  &.MuiGrid-item {
    padding-right: 1rem;
  }
`;

const ConditionGrid = styled(Grid)`
  &.MuiGrid-item {
    padding: 0;
  }
`;

const Title = styled.div`
  font-size: ${FONT_SIZES.title3};
  line-height: 1.4;
`;

const Condition = styled.div`
  padding-left: 25px;
  font-size: ${FONT_SIZES.body3};
  line-height: 1.4;
`;

const ButtonArea = styled.div`
  width: 100%;
  padding: 10px 20px 0;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -102%);
`;

const Overlay = styled.div`
  height: 46px;
  // 下のレイヤーの枠線に収まる様に横幅を調整
  width: calc(100% - 2px);
  margin-left: 1px;
  background: transparent linear-gradient(180deg, #ffffffe2 0%, ${PALETTE.white} 100%) 0% 0%
    no-repeat padding-box;
  position: absolute;
  bottom: 0;
  left: 0;
`;

type StyledIconProps = {
  $expanded: boolean;
};

const StyledIcon = styled(KeyboardArrowDownIcon)<StyledIconProps>`
  color: ${PALETTE.gray[500]};
  font-size: ${FONT_SIZES.body1};
  vertical-align: middle;
  transition: 0.44s ease;
  ${(props) =>
    props.$expanded &&
    css`
      transform: rotate(180deg);
      transform-origin: 50% 50%;
    `}
`;

type StyledLinkProps = {
  component: string;
};
const StyledLink = styled(Link)<StyledLinkProps>`
  display: block;
  margin: 0 auto 5px;
  &.MuiTypography-root {
    font-size: ${FONT_SIZES.note3};
    text-decoration: none;
    color: ${PALETTE.gray[500]};
  }
`;

const StyledContainerGrid = styled(ContainerGrid)`
  padding: 14px 0px 2px 27px;
  align-items: center;
`;

type ConditionProps = {
  age: string;
  gender: string;
  region: string;
  jobGroup: string;
  industry: string;
  marriageStatus: string;
  childrenStatus: string;
};

type Props = {
  conditions: ConditionProps;
  defExpand?: boolean;
};

const DetailedConditionsDisplayArea: React.FC<Props> = ({
  conditions,
  defExpand = false,
}): JSX.Element => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(defExpand);

  return (
    <Wrapper data-testid="uiParts-DetailedConditionsDisplayArea">
      <StyledCollapse
        in={expanded}
        $expanded={expanded}
        collapsedSize="162px"
        data-testid="DetailedConditionsDisplayWrapper"
      >
        {Object.keys(conditions).map((key) => (
          <ConditionItem key={key}>
            <StyledContainerGrid container spacing={2} columns={16}>
              <TitleGrid item xs={4}>
                <Title
                  dangerouslySetInnerHTML={{
                    __html: t(`quotas.conditions.titles.${key}`),
                  }}
                />
              </TitleGrid>
              <ConditionGrid item xs={12}>
                <Condition>{(conditions as Record<string, string>)[key]}</Condition>
              </ConditionGrid>
            </StyledContainerGrid>
          </ConditionItem>
        ))}
      </StyledCollapse>

      <ButtonArea>
        <Overlay />
        <StyledLink
          component="button"
          onClick={() => setExpanded(!expanded)}
          data-testid="ToggleDetailedConditionsDisplayBtn"
        >
          <StyledIcon $expanded={expanded} />
          {expanded
            ? t('quotas.conditions.closeDetailedConditionsDisplay')
            : t('quotas.conditions.showDetailedConditionsDisplay')}
        </StyledLink>
      </ButtonArea>
    </Wrapper>
  );
};

export default DetailedConditionsDisplayArea;
